import { isPresent } from "@/lib/utils"
import vuexStore from "@/store"
import Emitter from "tiny-emitter"
import router from "@/router"
import ApiClient from "@/lib/api-client"
import {
  ExtensionData,
  QueryOptions,
  QueryParams,
  DeleteQuery,
  UpdateParams
} from "./types"

class ApiService {
  static async post(endpoint: string, data: ExtensionData | ExtensionData[]) {
    const response = await ApiClient.post(endpoint, { data })
    return response.data
  }

  static async get(endpoint: string, params: QueryParams) {
    const response = await ApiClient.get(endpoint, { params })
    return response.data
  }

  static async put(endpoint: string, data: UpdateParams) {
    const response = await ApiClient.put(endpoint, data)
    return response.data
  }

  static async delete(endpoint: string, params: DeleteQuery) {
    const response = await ApiClient.delete(endpoint, { params })
    return response.data
  }
}

class EventManager {
  private readonly emitter: any
  private readonly unwatchers: any[] = []
  LOGGED_IN = "loggedIn"
  LOGGED_OUT = "loggedOut"

  PROPS_UPDATED = "propsUpdated"

  constructor() {
    this.emitter = new (Emitter as any)()
    this._initializeAuthWatcher()
  }

  on(event: string, callback: (...args: any[]) => void): void {
    this.emitter.on(event, callback)
  }

  emit(event: string, ...args: any[]): void {
    this.emitter.emit(event, ...args)
  }

  destroy(): void {
    this.unwatchers.forEach(unwatcher => unwatcher())
    this.emitter.off()
  }

  private _initializeAuthWatcher(): void {
    const unwatcher = vuexStore.watch(
      (_: any, getters: { [key: string]: any }) => getters["auth/currentUser"],
      (user: { id: string; name: string; email: string } | null) =>
        this.emit(isPresent(user) ? this.LOGGED_IN : this.LOGGED_OUT, user)
    )
    this.unwatchers.push(unwatcher)
  }
}

class ExtensionStore {
  constructor(private readonly extensionSlug: string) {}

  async set(key: string, value: any, created_at?: Date) {
    if (Array.isArray(value)) {
      // NOTE: For now we are not letting this so that it would be easier to aggregate
      throw new Error("Value cannot be an array")
    }

    return await ApiService.post(
      `/extensions/data/${this.extensionSlug}/${key}`,
      {
        value,
        created_at
      }
    )
  }

  async setMany(key: string, data: ExtensionData | ExtensionData[]) {
    return await ApiService.post(
      `/extensions/data/${this.extensionSlug}/${key}`,
      data
    )
  }

  async get(key: string | string[]) {
    const data = await ApiService.get(
      `/extensions/data/${this.extensionSlug}/${key}`,
      {
        query: { limit: 1 },
        member_only: true
      }
    )

    return data.results[0]
  }

  async query(key: string | string[], query: QueryOptions) {
    return await ApiService.get(
      `/extensions/data/${this.extensionSlug}/${key}`,
      {
        query
      }
    )
  }

  async queryMember(key: string | string[], query: QueryOptions) {
    return await ApiService.get(
      `/extensions/data/${this.extensionSlug}/${key}`,
      {
        query,
        member_only: true
      }
    )
  }

  async update(key: string, id: string, value: any) {
    return await ApiService.put(
      `/extensions/data/${this.extensionSlug}/${key}`,
      {
        id,
        value
      }
    )
  }

  async delete(key: string, query?: DeleteQuery) {
    const params: DeleteQuery = {}

    if (query?.id) {
      params.id = query.id
    }

    if (query?.filter) {
      params.filter = query.filter
    }

    return await ApiService.delete(
      `/extensions/data/${this.extensionSlug}/${key}`,
      params
    )
  }
}

class Sdk {
  readonly extensions: Record<string, ExtensionStore> = {}

  get events() {
    return eventManager
  }

  get components() {
    return componentsRegistry
  }

  // TODO: Rename to store
  extension(slug: string) {
    return (
      this.extensions[slug] ||
      (this.extensions[slug] = new ExtensionStore(slug))
    )
  }

  get user() {
    const user = vuexStore.getters["auth/currentUser"]

    return isPresent(user)
      ? {
          name: user.name,
          email: user.email,
          id: user.subscriberUuid
        }
      : null
  }

  async logout() {
    await vuexStore.dispatch("auth/logout")
  }

  async login() {
    router.push("#auth")
  }
}

class ComponentsRegistry {
  private readonly components: Record<
    string,
    { props: Record<string, any>; app: any }
  > = {}

  register(
    id: string,
    { props, app }: { props: Record<string, any>; app: any }
  ) {
    this.components[id] = { props, app }
  }

  unregister(id: string) {
    delete this.components[id]
  }

  setProps(id: string, props: Record<string, any>) {
    if (!this.components[id]) {
      this.components[id] = { props: {}, app: null }
    }

    Object.assign(this.components[id].props, props)

    eventManager.emit(`${eventManager.PROPS_UPDATED}:${id}`, props)
  }

  getProps(id: string) {
    return this.components[id]?.props || {}
  }

  get(id: string) {
    return this.components[id]
  }
}

const eventManager = new EventManager()
const componentsRegistry = new ComponentsRegistry()

export default Sdk
